// Editing operations on an efmodel document, used by the sketch editor. Each returns a new document
// and leaves the input untouched, so editors can keep earlier documents for undo. References stay
// consistent (keys, indexes, relationships, inheritance), and renames of things that exist in the
// base model are recorded in `renames` so comparisons pair the old and new names.
import { primaryKeyOf, rootOfPath, specAt } from '../model/logical';
import type { DeleteBehavior, EfModelDocument, EntitySpec, IndexSpec, ModelSpec, OwnedSpec, PropertySpec, RelationshipSpec, RenameSpec } from '../model/schema';

export class EditError extends Error {}

type Doc = EfModelDocument;
type Container = EntitySpec | OwnedSpec;

const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;

function checkName(name: string, what: string): void {
  if (!IDENTIFIER.test(name)) throw new EditError(`"${name}" is not a valid ${what} name: use letters, digits and _, not starting with a digit.`);
}

/** Replaces a key in a record, keeping the order of the others. */
function renameKey<T>(record: Record<string, T>, from: string, to: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).map(([k, v]) => [k === from ? to : k, v]));
}

function renamePath(path: string, from: string, to: string): string {
  if (path === from) return to;
  return path.startsWith(`${from}.`) ? `${to}${path.slice(from.length)}` : path;
}

const under = (path: string, root: string) => path === root || path.startsWith(`${root}.`);

function containerAt(model: ModelSpec, path: string): Container {
  const spec = specAt(model, path);
  if (!spec) throw new EditError(`No entity or owned type "${path}".`);
  return spec;
}

/** Applies `change` to a copy of the document. */
function edit(doc: Doc, change: (draft: Doc) => void): Doc {
  const draft = structuredClone(doc);
  change(draft);
  if (draft.renames && draft.renames.length === 0) delete draft.renames;
  return draft;
}

// ------------------------------------------------------------------ rename bookkeeping

/** The name an entity had in the base model, following recorded renames back. */
function originalEntity(doc: Doc, key: string): string {
  return doc.renames?.find((r) => !r.entity && r.to === key)?.from ?? key;
}

function recordEntityRename(doc: Doc, from: string, to: string, base: ModelSpec | undefined): void {
  const renames: RenameSpec[] = doc.renames ?? [];
  const existing = renames.find((r) => !r.entity && r.to === from);
  if (existing) existing.to = to;
  else if (!base || base.entities[from]) renames.push({ from, to });
  for (const r of renames) if (r.entity) r.entity = renamePath(r.entity, from, to);
  doc.renames = renames.filter((r) => r.entity || r.from !== r.to);
}

function recordPropertyRename(doc: Doc, path: string, from: string, to: string, base: ModelSpec | undefined): void {
  const renames: RenameSpec[] = doc.renames ?? [];
  const existing = renames.find((r) => r.entity === path && r.to === from);
  if (existing) existing.to = to;
  else {
    const { root, rest } = rootOfPath(doc.model, path);
    const inBase = !base || specAt(base, [originalEntity(doc, root), ...rest].join('.'))?.properties[from] !== undefined;
    if (inBase) renames.push({ entity: path, from, to });
  }
  doc.renames = renames.filter((r) => !r.entity || r.from !== r.to);
}

// ------------------------------------------------------------------ entities

export interface NewEntityOptions {
  /** Type of the generated `Id` key (default int). */
  keyType?: string;
  base?: string;
}

export function addEntity(doc: Doc, name: string, options: NewEntityOptions = {}): Doc {
  checkName(name, 'entity');
  if (doc.model.entities[name]) throw new EditError(`There is already an entity "${name}".`);
  if (options.base && !doc.model.entities[options.base]) throw new EditError(`No entity "${options.base}".`);
  return edit(doc, (d) => {
    const keyType = options.keyType ?? 'int';
    d.model.entities[name] = options.base
      ? { base: options.base, properties: {} }
      : { key: ['Id'], properties: { Id: { type: keyType, required: true, ...(keyType === 'int' || keyType === 'long' ? { generated: 'onAdd' as const } : {}) } } };
  });
}

export function renameEntity(doc: Doc, from: string, to: string, base?: ModelSpec): Doc {
  checkName(to, 'entity');
  if (!doc.model.entities[from]) throw new EditError(`No entity "${from}".`);
  if (from === to) return doc;
  if (doc.model.entities[to]) throw new EditError(`There is already an entity "${to}".`);
  return edit(doc, (d) => {
    const m = d.model;
    m.entities = renameKey(m.entities, from, to);
    const e = m.entities[to]!;
    if (e.clrName) {
      const dot = e.clrName.lastIndexOf('.');
      e.clrName = dot >= 0 ? `${e.clrName.slice(0, dot + 1)}${to}` : to;
    }
    for (const other of Object.values(m.entities)) if (other.base === from) other.base = to;
    for (const r of m.relationships) {
      r.dependent = renamePath(r.dependent, from, to);
      r.principal = renamePath(r.principal, from, to);
    }
    recordEntityRename(d, from, to, base);
  });
}

/** Removes an entity and its relationships. Entities that derive from it must be removed first. */
export function removeEntity(doc: Doc, key: string): Doc {
  if (!doc.model.entities[key]) throw new EditError(`No entity "${key}".`);
  const derived = Object.entries(doc.model.entities).filter(([, e]) => e.base === key).map(([k]) => k);
  if (derived.length) throw new EditError(`${derived.join(', ')} derive${derived.length === 1 ? 's' : ''} from ${key}; remove ${derived.length === 1 ? 'it' : 'them'} or change the base first.`);
  return edit(doc, (d) => {
    delete d.model.entities[key];
    d.model.relationships = d.model.relationships.filter((r) => !under(r.dependent, key) && !under(r.principal, key));
    d.renames = (d.renames ?? []).filter((r) => (r.entity ? !under(r.entity, key) : r.to !== key));
  });
}

export interface EntityPatch {
  keyless?: boolean;
  base?: string | null;
  table?: string | null;
  schema?: string | null;
  comment?: string | null;
  strategy?: 'tph' | 'tpt' | 'tpc' | null;
}

export function updateEntity(doc: Doc, key: string, patch: EntityPatch): Doc {
  const current = doc.model.entities[key];
  if (!current) throw new EditError(`No entity "${key}".`);
  if (patch.base) {
    if (!doc.model.entities[patch.base]) throw new EditError(`No entity "${patch.base}".`);
    for (let b: string | undefined = patch.base; b; b = doc.model.entities[b]?.base) {
      if (b === key) throw new EditError(`${key} cannot derive from ${patch.base}: that would be a cycle.`);
    }
  }
  return edit(doc, (d) => {
    const e = d.model.entities[key]!;
    // An entity that stops deriving or being keyless gets an Id key.
    const ensureKey = () => {
      if (e.base || e.key || e.keyless) return;
      if (!e.properties['Id']) e.properties = { Id: { type: 'int', required: true, generated: 'onAdd' }, ...e.properties };
      e.key = ['Id'];
    };
    if (patch.base !== undefined) {
      if (patch.base) {
        e.base = patch.base;
        delete e.key;
        delete e.keyless;
        if (e.mapping) delete e.mapping.strategy;
      } else {
        delete e.base;
        ensureKey();
      }
    }
    if (patch.keyless !== undefined) {
      if (patch.keyless) {
        e.keyless = true;
        delete e.key;
      } else {
        delete e.keyless;
        ensureKey();
      }
    }
    const mapping = { ...e.mapping };
    if (patch.table !== undefined) {
      if (patch.table === null || patch.table === '') delete mapping.table;
      else mapping.table = patch.table;
    }
    if (patch.schema !== undefined) {
      if (!patch.schema) delete mapping.schema;
      else mapping.schema = patch.schema;
    }
    if (patch.strategy !== undefined) {
      if (!patch.strategy) delete mapping.strategy;
      else mapping.strategy = patch.strategy;
    }
    if (Object.keys(mapping).length) e.mapping = mapping;
    else delete e.mapping;
    if (patch.comment !== undefined) {
      if (patch.comment) e.comment = patch.comment;
      else delete e.comment;
    }
  });
}

// ------------------------------------------------------------------ properties

export function addProperty(doc: Doc, path: string, name: string, spec: PropertySpec = { type: 'string', required: true }): Doc {
  checkName(name, 'property');
  const c = containerAt(doc.model, path);
  if (c.properties[name] || c.owned?.[name] || c.complex?.[name]) throw new EditError(`"${path}" already has a member "${name}".`);
  return edit(doc, (d) => {
    containerAt(d.model, path).properties[name] = spec;
  });
}

function renameInList(list: string[] | undefined, from: string, to: string): string[] | undefined {
  return list?.map((x) => (x === from ? to : x));
}

export function renameProperty(doc: Doc, path: string, from: string, to: string, base?: ModelSpec): Doc {
  checkName(to, 'property');
  const c = containerAt(doc.model, path);
  if (!c.properties[from]) throw new EditError(`"${path}" has no property "${from}".`);
  if (from === to) return doc;
  if (c.properties[to] || c.owned?.[to] || c.complex?.[to]) throw new EditError(`"${path}" already has a member "${to}".`);
  return edit(doc, (d) => {
    const target = containerAt(d.model, path);
    target.properties = renameKey(target.properties, from, to);
    if (target.key) target.key = renameInList(target.key, from, to);
    if ('ownerKey' in target) target.ownerKey = renameInList(target.ownerKey, from, to)!;
    const entity = target as EntitySpec;
    if (entity.alternateKeys) entity.alternateKeys = entity.alternateKeys.map((k) => renameInList(k, from, to)!);
    if (target.indexes) for (const ix of target.indexes) ix.properties = renameInList(ix.properties, from, to)!;
    if (target.mapping?.discriminator === from) target.mapping.discriminator = to;
    if (target.seed) target.seed = target.seed.map((row) => renameKey(row, from, to));
    // Relationships, including those of derived types that see this property through inheritance.
    const sees = (p: string) => {
      if (p === path) return true;
      let e = d.model.entities[p];
      while (e?.base) {
        if (e.base === path) return true;
        e = d.model.entities[e.base];
      }
      return false;
    };
    for (const r of d.model.relationships) {
      if (sees(r.dependent)) r.foreignKey = renameInList(r.foreignKey, from, to)!;
      if (sees(r.principal) && r.principalKey) r.principalKey = renameInList(r.principalKey, from, to);
    }
    recordPropertyRename(d, path, from, to, base);
  });
}

/**
 * Removes a property, with the indexes and relationships that use it. Key properties must be
 * taken out of the key first.
 */
export function removeProperty(doc: Doc, path: string, name: string): Doc {
  const c = containerAt(doc.model, path);
  if (!c.properties[name]) throw new EditError(`"${path}" has no property "${name}".`);
  if (c.key?.includes(name) || ('ownerKey' in c && c.ownerKey.includes(name))) throw new EditError(`"${name}" is part of the key of "${path}"; change the key first.`);
  return edit(doc, (d) => {
    const target = containerAt(d.model, path);
    delete target.properties[name];
    const entity = target as EntitySpec;
    if (entity.alternateKeys) {
      entity.alternateKeys = entity.alternateKeys.filter((k) => !k.includes(name));
      if (!entity.alternateKeys.length) delete entity.alternateKeys;
    }
    if (target.indexes) {
      target.indexes = target.indexes.filter((ix) => !ix.properties.includes(name));
      if (!target.indexes.length) delete target.indexes;
    }
    if (target.mapping?.discriminator === name) delete target.mapping.discriminator;
    if (target.seed) target.seed = target.seed.map(({ [name]: _removed, ...row }) => row);
    d.model.relationships = d.model.relationships.filter((r) => !(r.dependent === path && r.foreignKey.includes(name)) && !(r.principal === path && r.principalKey?.includes(name)));
    d.renames = (d.renames ?? []).filter((r) => !(r.entity === path && r.to === name));
  });
}

/** Changes a property's facets. `undefined` leaves a facet as it is; `null` removes it. */
export function updateProperty(doc: Doc, path: string, name: string, patch: { [K in keyof PropertySpec]?: PropertySpec[K] | null }): Doc {
  const c = containerAt(doc.model, path);
  if (!c.properties[name]) throw new EditError(`"${path}" has no property "${name}".`);
  return edit(doc, (d) => {
    const p = containerAt(d.model, path).properties[name]! as Record<string, unknown>;
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) continue;
      if (v === null) delete p[k];
      else p[k] = v;
    }
    if (p['type'] === undefined || p['type'] === '') throw new EditError('A property needs a type.');
  });
}

export function setKey(doc: Doc, key: string, properties: string[]): Doc {
  const e = doc.model.entities[key];
  if (!e) throw new EditError(`No entity "${key}".`);
  if (e.base) throw new EditError(`${key} inherits its key from ${e.base}.`);
  for (const p of properties) if (!e.properties[p]) throw new EditError(`${key} has no property "${p}".`);
  return edit(doc, (d) => {
    const target = d.model.entities[key]!;
    if (properties.length) {
      target.key = properties;
      delete target.keyless;
      for (const p of properties) target.properties[p]!.required = true;
    } else {
      delete target.key;
      target.keyless = true;
    }
  });
}

export function setIndexes(doc: Doc, path: string, indexes: IndexSpec[]): Doc {
  const c = containerAt(doc.model, path);
  for (const ix of indexes) for (const p of ix.properties) if (!c.properties[p]) throw new EditError(`"${path}" has no property "${p}".`);
  return edit(doc, (d) => {
    const target = containerAt(d.model, path);
    if (indexes.length) target.indexes = indexes;
    else delete target.indexes;
  });
}

// ------------------------------------------------------------------ relationships

export interface NewRelationship {
  /** The entity that holds the foreign key (the "many" side of one-to-many). */
  dependent: string;
  principal: string;
  cardinality?: 'one-to-many' | 'one-to-one';
  required?: boolean;
  onDelete?: DeleteBehavior;
  /** Existing properties to use; new ones named `<Principal><KeyProperty>` are added otherwise. */
  foreignKey?: string[];
  dependentNavigation?: string | null;
  principalNavigation?: string | null;
}

function plural(name: string): string {
  if (/(s|x|z|ch|sh)$/.test(name)) return `${name}es`;
  if (/[^aeiou]y$/.test(name)) return `${name.slice(0, -1)}ies`;
  return `${name}s`;
}

const shortName = (path: string) => path.slice(path.lastIndexOf('.') + 1);

function freeName(taken: (n: string) => boolean, wanted: string): string {
  let name = wanted;
  for (let i = 2; taken(name); i++) name = `${wanted}${i}`;
  return name;
}

/** Adds a relationship, creating foreign key properties (and navigations) as EF Core conventions would. */
export function addRelationship(doc: Doc, spec: NewRelationship): Doc {
  const dependent = containerAt(doc.model, spec.dependent);
  const principal = containerAt(doc.model, spec.principal);
  const principalKey = (spec.principal.includes('.') && !doc.model.entities[spec.principal] ? (principal as OwnedSpec).key ?? (principal as OwnedSpec).ownerKey : primaryKeyOf(doc.model, spec.principal)) ?? [];
  if (!principalKey.length) throw new EditError(`${spec.principal} has no key to reference.`);
  const cardinality = spec.cardinality ?? 'one-to-many';
  const required = spec.required ?? true;
  const principalName = shortName(spec.principal);
  const dependentName = shortName(spec.dependent);
  const members = (c: Container) => (n: string) => !!c.properties[n] || !!c.owned?.[n] || !!c.complex?.[n];
  if (spec.foreignKey && spec.foreignKey.length !== principalKey.length) throw new EditError(`The foreign key needs ${principalKey.length} propert${principalKey.length === 1 ? 'y' : 'ies'}.`);
  for (const p of spec.foreignKey ?? []) if (!dependent.properties[p]) throw new EditError(`${spec.dependent} has no property "${p}".`);

  return edit(doc, (d) => {
    const dep = containerAt(d.model, spec.dependent);
    const prin = containerAt(d.model, spec.principal);
    const prinProps = { ...prin.properties };
    for (let b = d.model.entities[spec.principal]?.base; b; b = d.model.entities[b]?.base) Object.assign(prinProps, d.model.entities[b]!.properties);
    const foreignKey =
      spec.foreignKey ??
      principalKey.map((k) => {
        const name = freeName(members(dep), `${principalName}${k}`);
        const keyProp = prinProps[k];
        dep.properties[name] = { type: keyProp?.type ?? 'int', required, ...(keyProp?.maxLength ? { maxLength: keyProp.maxLength } : {}) };
        return name;
      });
    const navTaken = (c: Container, extra: string[]) => (n: string) => members(c)(n) || extra.includes(n);
    const usedDep = d.model.relationships.filter((r) => r.dependent === spec.dependent).map((r) => r.dependentNavigation ?? '');
    const usedPrin = d.model.relationships.filter((r) => r.principal === spec.principal).map((r) => r.principalNavigation ?? '');
    const dependentNavigation = spec.dependentNavigation === null ? undefined : (spec.dependentNavigation ?? freeName(navTaken(dep, usedDep), principalName));
    const principalNavigation =
      spec.principalNavigation === null ? undefined : (spec.principalNavigation ?? freeName(navTaken(prin, usedPrin), cardinality === 'one-to-many' ? plural(dependentName) : dependentName));
    const relationship: RelationshipSpec = {
      dependent: spec.dependent,
      principal: spec.principal,
      foreignKey,
      cardinality,
      required,
      onDelete: spec.onDelete ?? (required ? 'cascade' : 'clientSetNull'),
      ...(dependentNavigation ? { dependentNavigation } : {}),
      ...(principalNavigation ? { principalNavigation } : {}),
    };
    d.model.relationships.push(relationship);
    // EF Core indexes foreign keys; one-to-one foreign keys are unique.
    const indexes = dep.indexes ?? [];
    if (!indexes.some((ix) => ix.properties.join() === foreignKey.join())) {
      indexes.push(cardinality === 'one-to-one' ? { properties: foreignKey, unique: true } : { properties: foreignKey });
      dep.indexes = indexes;
    }
  });
}

export function updateRelationship(doc: Doc, index: number, patch: Partial<Pick<RelationshipSpec, 'cardinality' | 'required' | 'onDelete' | 'requiredDependent' | 'dependentNavigation' | 'principalNavigation'>>): Doc {
  if (!doc.model.relationships[index]) throw new EditError(`No relationship ${index}.`);
  return edit(doc, (d) => {
    const r = d.model.relationships[index]!;
    Object.assign(r, patch);
    for (const k of ['dependentNavigation', 'principalNavigation', 'requiredDependent'] as const) if (r[k] === undefined || r[k] === '' || r[k] === false) delete r[k];
    if (r.cardinality !== 'one-to-one') delete r.requiredDependent;
    const dep = containerAt(d.model, r.dependent);
    // Keep the foreign key's nullability and uniqueness in line with the relationship.
    if (patch.required !== undefined) for (const p of r.foreignKey) if (dep.properties[p]) dep.properties[p]!.required = patch.required;
    if (patch.cardinality !== undefined) {
      const ix = dep.indexes?.find((x) => x.properties.join() === r.foreignKey.join());
      if (ix) {
        if (patch.cardinality === 'one-to-one') ix.unique = true;
        else delete ix.unique;
      }
    }
  });
}

/** Removes a relationship and, unless they are used elsewhere, its foreign key properties and index. */
export function removeRelationship(doc: Doc, index: number, options: { keepForeignKey?: boolean } = {}): Doc {
  const r = doc.model.relationships[index];
  if (!r) throw new EditError(`No relationship ${index}.`);
  return edit(doc, (d) => {
    d.model.relationships.splice(index, 1);
    if (options.keepForeignKey) return;
    const dep = specAt(d.model, r.dependent);
    if (!dep) return;
    const usedElsewhere = (p: string) =>
      dep.key?.includes(p) || ('ownerKey' in dep && dep.ownerKey.includes(p)) || d.model.relationships.some((x) => x.dependent === r.dependent && x.foreignKey.includes(p));
    for (const p of r.foreignKey) if (!usedElsewhere(p)) delete dep.properties[p];
    if (dep.indexes) {
      dep.indexes = dep.indexes.filter((ix) => ix.properties.every((p) => dep.properties[p] !== undefined));
      if (!dep.indexes.length) delete dep.indexes;
    }
  });
}

// ------------------------------------------------------------------ owned types

/**
 * Turns an entity into an owned type of the entity it depends on: the relationship to the owner
 * becomes ownership (one-to-one gives OwnsOne, one-to-many OwnsMany), and relationships to it now
 * point at `Owner.Navigation`.
 */
export function makeOwned(doc: Doc, key: string, options: { owner?: string; navigation?: string } = {}): Doc {
  const e = doc.model.entities[key];
  if (!e) throw new EditError(`No entity "${key}".`);
  if (e.base || Object.values(doc.model.entities).some((x) => x.base === key)) throw new EditError(`${key} is part of an inheritance hierarchy and cannot become owned.`);
  const candidates = doc.model.relationships
    .map((r, i) => [r, i] as const)
    .filter(([r]) => r.dependent === key && doc.model.entities[r.principal] && (!options.owner || r.principal === options.owner));
  if (candidates.length === 0) throw new EditError(`${key} has no relationship to ${options.owner ?? 'an owner'}; add one first (from ${key} to its owner).`);
  if (candidates.length > 1) throw new EditError(`${key} depends on several entities (${candidates.map(([r]) => r.principal).join(', ')}); choose the owner.`);
  const [link, linkIndex] = candidates[0]!;
  if (doc.model.relationships.some((r) => r.principal === key && r.dependent !== key && !doc.model.entities[r.dependent])) throw new EditError(`${key} has owned dependents; move them first.`);
  const owner = link.principal;
  const collection = link.cardinality === 'one-to-many';
  const navigation = options.navigation ?? link.principalNavigation ?? (collection ? plural(key) : key);
  const ownerSpec = doc.model.entities[owner]!;
  if (ownerSpec.properties[navigation] || ownerSpec.owned?.[navigation] || ownerSpec.complex?.[navigation]) throw new EditError(`${owner} already has a member "${navigation}".`);

  return edit(doc, (d) => {
    const entity = d.model.entities[key]!;
    const owned: OwnedSpec = {
      type: key,
      ...(entity.clrName ? { clrName: entity.clrName } : {}),
      ...(collection ? { collection: true } : {}),
      ...(link.required && !collection ? { required: true } : {}),
      ownerKey: link.foreignKey,
      ...(collection ? { key: [...link.foreignKey, ...(entity.key ?? []).filter((k) => !link.foreignKey.includes(k))] } : {}),
      properties: entity.properties,
      ...(entity.owned ? { owned: entity.owned } : {}),
      ...(entity.complex ? { complex: entity.complex } : {}),
      ...(entity.indexes ? { indexes: entity.indexes.filter((ix) => ix.properties.join() !== link.foreignKey.join()) } : {}),
      ...(entity.checks ? { checks: entity.checks } : {}),
      ...(entity.comment ? { comment: entity.comment } : {}),
    };
    if (!collection) {
      // OwnsOne shares the owner's key: the foreign key is the owned type's key.
      for (const k of entity.key ?? []) if (!link.foreignKey.includes(k)) delete owned.properties[k];
    }
    if (owned.indexes && !owned.indexes.length) delete owned.indexes;
    const ownerTarget = d.model.entities[owner]!;
    ownerTarget.owned = { ...ownerTarget.owned, [navigation]: owned };
    delete d.model.entities[key];
    const path = `${owner}.${navigation}`;
    d.model.relationships = d.model.relationships
      .filter((_, i) => i !== linkIndex)
      .map((r) => ({ ...r, dependent: renamePath(r.dependent, key, path), principal: renamePath(r.principal, key, path) }));
    d.renames = (d.renames ?? []).filter((r) => (r.entity ? !under(r.entity, key) : r.to !== key));
  });
}

// ------------------------------------------------------------------ documents

/** Starts a sketch from a model: a copy with its own title that records what it is based on. */
export function branchDocument(from: Doc, title: string, basedOn: NonNullable<Doc['basedOn']>): Doc {
  const doc = structuredClone(from);
  delete doc.source;
  delete doc.renames;
  return { ...doc, title, basedOn };
}
